import React, { useState, useEffect, useRef } from "react";
import {
  motion,
  AnimatePresence,
  useScroll,
  useTransform,
  useInView,
} from "framer-motion";
import {
  Menu,
  X,
  Sun,
  Moon,
  ArrowRight,
  ChevronDown,
  Mail,
  Linkedin,
  Github,
  Calendar,
  ExternalLink,
} from "lucide-react";
import "./App.css";
import { inject } from "@vercel/analytics";

inject();

// ===== TRANSLATIONS =====
const translations = {
  en: {
    nav: {
      home: "Home",
      consulting: "Consulting",
      about: "About",
      services: "Services",
      projects: "Projects",
      experience: "Experience",
      contact: "Contact",
    },
    hero: {
      greeting: "Hello, I'm",
      name: "Andre Lacke",
      roles: [
        "CTO",
        "Founder",
        "AI Solutions Architect",
        "Technical Consultant",
        "Full Stack Engineer",
        "Automation Specialist",
      ],
      description:
        "I help businesses harness AI to automate workflows, build intelligent systems, and scale smarter",
      cta: "Start a Conversation",
      scroll: "Explore",
    },
    aiFor: {
      industries: [
        "Engineering Teams",
        "Company Leadership",
        "Business Automation",
        "Small & Medium Business",
        "Enterprise Organizations",
        "Content Creators",
        "E-commerce",
      ],
    },
    latestProjects: {
      label: "Latest Projects",
      title: "Recent",
      titleAccent: "work",
      items: [
        { name: "harmoni.engineer", url: "https://harmoni.engineer" },
        { name: "Alphana Visuals", url: "https://visuals.alphana.ai" },
        {
          name: "Pecorino Guatemala",
          url: "https://pecorino-zeta.vercel.app/en",
        },
      ],
    },
    about: {
      label: "About",
      title: "Building the future,",
      titleAccent: "one product at a time",
      bio: "I'm a technology leader who transforms ambitious ideas into production-ready AI systems. With experience as CTO and founding engineer at multiple startups, I've architected everything from intelligent SQL agents to Ai agent workflow engines that serve thousands of users.",
      bio2: "My expertise spans the full stack—from crafting beautiful, performant frontends to designing scalable cloud architectures and implementing cutting-edge AI solutions. I believe in building technology that feels magical while solving real business problems.",
      stats: [
        { value: "7+", label: "Years Building" },
        { value: "25+", label: "Products Shipped" },
        { value: "3x", label: "Founder" },
      ],
    },
    consulting: {
      label: "Consulting",
      title: "AI expertise",
      titleAccent: "on your terms",
      subtitle: "",
      matrixIntro:
        "Choose your timeline, choose your depth—or combine both tracks for comprehensive transformation.",
      timelines: ["Quick Start", "Project-Based", "Ongoing"],
      tracks: {
        strategy: {
          label: "Strategy",
          description: "Assessment, roadmaps & action plans",
          options: [
            {
              title: "AI Readiness Audit",
              desc: "Focused assessment with prioritized recommendations your team can implement immediately",
            },
            {
              title: "Transformation Roadmap",
              desc: "Comprehensive strategy with phased implementation plan and success metrics",
            },
            {
              title: "Strategic Partnership",
              desc: "Ongoing advisory, quarterly reviews, and evolving AI strategy alignment",
            },
          ],
        },
        implementation: {
          label: "Implementation",
          description: "Agents, apps, workflows & deployment",
          options: [
            {
              title: "Quick Deploy",
              desc: "Targeted deployment—AI agents, workflow automation, static sites, or internal tools",
            },
            {
              title: "Full Build-Out",
              desc: "End-to-end delivery: applications, ambient agents, training systems, and production infrastructure",
            },
            {
              title: "Embedded Engineering",
              desc: "Ongoing technical partnership—new capabilities, maintenance, and team enablement",
            },
          ],
        },
      },
      pathNote:
        "Most clients combine both tracks—strategy informs implementation, implementation validates strategy.",
      cta: "Book a Discovery Call",
      ctaSubtext: "Free 30-minute consultation to discuss your AI goals",
    },
    services: {
      label: "Services",
      title: "How I can",
      titleAccent: "help you succeed",
      items: [
        {
          icon: "cpu",
          title: "AI Consulting",
          description:
            "Strategic guidance on AI solutions that deliver real ROI. LLM selection, system architecture, and deployment strategies for SMBs to enterprise.",
          features: [
            "AI Strategy",
            "Architecture Design",
            "LLM Integration",
            "RAG & Agents",
          ],
        },
        {
          icon: "workflow",
          title: "Workflow Automation",
          description:
            "Intelligent automation for businesses of any size. Content creation pipelines, data workflows, and business process optimization.",
          features: [
            "Content Automation",
            "Data Pipelines",
            "Process Optimization",
            "System Integration",
          ],
        },
        {
          icon: "code",
          title: "Web Development",
          description:
            "Beautiful, performant websites and web applications. From static sites to full-stack apps, built with modern technologies.",
          features: [
            "Static Sites",
            "Web Apps",
            "React & Next.js",
            "Full Stack",
          ],
        },
        {
          icon: "briefcase",
          title: "Training & CTO",
          description:
            "Upskill teams with hands-on workshops. Strategic tech leadership, architecture reviews, and scaling guidance.",
          features: [
            "AI Workshops",
            "Team Mentorship",
            "Tech Strategy",
            "Security",
          ],
        },
      ],
      cta: "Ready to elevate your digital presence?",
      ctaButton: "Get in Touch",
    },
    experience: {
      label: "Experience",
      title: "Career",
      titleAccent: "timeline",
      items: [
        {
          role: "CEO & Founder",
          company: "Harmoni.engineer",
          period: "2026 – Present",
          highlight: "AI engineering studio",
          type: "Full-time",
        },
        {
          role: "CTO",
          company: "Alphana",
          period: "2024 – Present",
          highlight: "Content creation platform",
          type: "Full-time",
        },
        {
          role: "Founding Engineer (AI)",
          company: "City as a School",
          period: "2023 – 2025",
          highlight: "AI tutor & career guidance",
          type: "Part-time",
        },
        {
          role: "Co-Founder & Chief AI Officer",
          company: "Chipp.AI",
          period: "2023 – 2024",
          highlight: "AI Agent company",
          type: "Full-time",
        },
        {
          role: "Frontend Engineer",
          company: "Ghost Creativ",
          period: "2022 – 2023",
          highlight: "Enterprise web apps",
          type: "Contract",
        },
        {
          role: "Frontend Engineer",
          company: "Stealth",
          period: "2022 – 2023",
          highlight: "Next.js templates",
          type: "Contract",
        },
      ],
    },
    contact: {
      label: "Contact",
      title: "Let's create",
      titleAccent: "something extraordinary",
      subtitle: "Have a project in mind? I'd love to hear about it.",
      form: {
        name: "Your name",
        email: "Your email",
        service: "How can I help?",
        message: "Tell me about your project",
        send: "Send Message",
        continue: "Continue",
        back: "Back",
      },
      serviceOptions: [
        "AI Readiness Audit",
        "Transformation Roadmap",
        "Strategic Partnership",
        "Quick Deploy",
        "Full Build-Out",
        "Embedded Engineering",
        "Web Development",
        "Training & CTO",
        "Other",
      ],
      step2: {
        timeline: "What timeline works for you?",
        timelineOptions: ["Quick Start", "Project-Based", "Ongoing"],
        details: "Tell us more about your project",
        detailsPlaceholder:
          "What challenges are you facing? What are your goals?",
      },
      or: "Or reach out directly",
      schedule: "Schedule a Call",
    },
    footer: {
      tagline: "Crafting intelligent solutions",
      rights: "All rights reserved",
    },
  },
  es: {
    nav: {
      home: "Inicio",
      consulting: "Consultoría",
      about: "Sobre Mí",
      services: "Servicios",
      projects: "Proyectos",
      experience: "Experiencia",
      contact: "Contacto",
    },
    hero: {
      greeting: "Hola, soy",
      name: "Andre Lacke",
      roles: [
        "CTO",
        "Fundador",
        "Arquitecto de Soluciones IA",
        "Consultor Técnico",
        "Ingeniero Full Stack",
        "Especialista en Automatización",
      ],
      description:
        "Ayudo a empresas a aprovechar la IA para automatizar flujos, construir sistemas inteligentes y escalar de forma más inteligente",
      cta: "Iniciar Conversación",
      scroll: "Explorar",
    },
    aiFor: {
      industries: [
        "Equipos de Ingeniería",
        "Liderazgo Empresarial",
        "Automatización de Negocios",
        "PyMEs",
        "Grandes Empresas",
        "Creadores de Contenido",
        "E-commerce",
      ],
    },
    latestProjects: {
      label: "Proyectos Recientes",
      title: "Trabajo",
      titleAccent: "reciente",
      items: [
        { name: "harmoni.engineer", url: "https://harmoni.engineer" },
        { name: "Alphana Visuals", url: "https://visuals.alphana.ai" },
        {
          name: "Pecorino Guatemala",
          url: "https://pecorino-zeta.vercel.app/en",
        },
      ],
    },
    about: {
      label: "Sobre Mí",
      title: "Construyendo el futuro,",
      titleAccent: "un producto a la vez",
      bio: "Soy un líder tecnológico que transforma ideas ambiciosas en sistemas de IA listos para producción. Con experiencia como CTO e ingeniero fundador en múltiples startups, he arquitecturado desde agentes SQL inteligentes hasta motores de flujo de trabajo de Agentes de IA que sirven a miles de usuarios.",
      bio2: "Mi experiencia abarca todo el stack—desde crear frontends hermosos y performantes hasta diseñar arquitecturas cloud escalables e implementar soluciones de IA de vanguardia. Creo en construir tecnología que se sienta mágica mientras resuelve problemas reales de negocio.",
      stats: [
        { value: "7+", label: "Años Construyendo" },
        { value: "25+", label: "Productos Lanzados" },
        { value: "3x", label: "Fundador" },
      ],
    },
    consulting: {
      label: "Consultoría",
      title: "Experiencia en IA",
      titleAccent: "a tu medida",
      subtitle: "",
      matrixIntro:
        "Elige tu timeline, elige tu profundidad—o combina ambas vías para una transformación completa.",
      timelines: ["Inicio Rápido", "Por Proyecto", "Continuo"],
      tracks: {
        strategy: {
          label: "Estrategia",
          description: "Evaluación, hojas de ruta y planes de acción",
          options: [
            {
              title: "Auditoría IA",
              desc: "Evaluación enfocada con recomendaciones priorizadas que tu equipo puede implementar inmediatamente",
            },
            {
              title: "Hoja de Ruta",
              desc: "Estrategia integral con plan de implementación por fases y métricas de éxito",
            },
            {
              title: "Asesoría Continua",
              desc: "Consultoría continua, revisiones trimestrales y alineación estratégica de IA",
            },
          ],
        },
        implementation: {
          label: "Implementación",
          description: "Agentes, apps, flujos y despliegue",
          options: [
            {
              title: "Despliegue Rápido",
              desc: "Implementación específica—agentes IA, automatización, sitios web o herramientas internas",
            },
            {
              title: "Construcción Completa",
              desc: "Entrega integral: aplicaciones, agentes ambientales, sistemas de capacitación e infraestructura",
            },
            {
              title: "Ingeniería Integrada",
              desc: "Asociación técnica continua—nuevas capacidades, mantenimiento y habilitación de equipos",
            },
          ],
        },
      },
      pathNote:
        "La mayoría combina ambas vías—la estrategia informa la implementación, la implementación valida la estrategia.",
      cta: "Agenda una Llamada",
      ctaSubtext:
        "Consulta gratuita de 30 minutos para discutir tus metas de IA",
    },
    services: {
      label: "Servicios",
      title: "Cómo puedo",
      titleAccent: "ayudarte a triunfar",
      items: [
        {
          icon: "cpu",
          title: "Consultoría IA",
          description:
            "Orientación estratégica en soluciones IA con ROI real. Selección de LLM, arquitectura de sistemas y estrategias de despliegue para PyMEs y empresas.",
          features: [
            "Estrategia IA",
            "Diseño de Arquitectura",
            "Integración LLM",
            "RAG y Agentes",
          ],
        },
        {
          icon: "workflow",
          title: "Automatización",
          description:
            "Automatización inteligente para negocios de cualquier tamaño. Pipelines de contenido, flujos de datos y optimización de procesos.",
          features: [
            "Automatización de Contenido",
            "Pipelines de Datos",
            "Optimización",
            "Integración",
          ],
        },
        {
          icon: "code",
          title: "Desarrollo Web",
          description:
            "Sitios web y aplicaciones hermosas y de alto rendimiento. Desde sitios estáticos hasta apps full-stack con tecnologías modernas.",
          features: [
            "Sitios Estáticos",
            "Apps Web",
            "React & Next.js",
            "Full Stack",
          ],
        },
        {
          icon: "briefcase",
          title: "Formación y CTO",
          description:
            "Capacita equipos con talleres prácticos. Liderazgo tecnológico estratégico, revisiones de arquitectura y guía de escalamiento.",
          features: ["Talleres IA", "Mentoría", "Estrategia Tech", "Seguridad"],
        },
      ],
      cta: "¿Listo para elevar tu presencia digital?",
      ctaButton: "Contáctame",
    },
    experience: {
      label: "Experiencia",
      title: "Trayectoria",
      titleAccent: "profesional",
      items: [
        {
          role: "CEO y Fundador",
          company: "Harmoni.engineer",
          period: "2026 – Presente",
          highlight: "Estudio de ingeniería de IA",
          type: "Tiempo completo",
        },
        {
          role: "CTO",
          company: "Alphana",
          period: "2024 – Presente",
          highlight: "Plataforma de creación de contenido",
          type: "Tiempo completo",
        },
        {
          role: "Ingeniero Fundador (IA)",
          company: "City as a School",
          period: "2023 – 2025",
          highlight: "Tutor de IA y orientación profesional",
          type: "Medio tiempo",
        },
        {
          role: "Co-Fundador y Director de IA",
          company: "Chipp.AI",
          period: "2023 – 2024",
          highlight: "Compañía de Agentes IA",
          type: "Tiempo completo",
        },
        {
          role: "Ingeniero Frontend",
          company: "Ghost Creativ",
          period: "2022 – 2023",
          highlight: "Aplicaciones web empresariales",
          type: "Contrato",
        },
        {
          role: "Ingeniero Frontend",
          company: "Stealth",
          period: "2022 – 2023",
          highlight: "Plantillas Next.js",
          type: "Contrato",
        },
      ],
    },
    contact: {
      label: "Contacto",
      title: "Creemos algo",
      titleAccent: "extraordinario juntos",
      subtitle: "¿Tienes un proyecto en mente? Me encantaría escucharlo.",
      form: {
        name: "Tu nombre",
        email: "Tu email",
        service: "¿Cómo puedo ayudar?",
        message: "Cuéntame sobre tu proyecto",
        send: "Enviar Mensaje",
        continue: "Continuar",
        back: "Atrás",
      },
      serviceOptions: [
        "Auditoría IA",
        "Hoja de Ruta de Transformación",
        "Asociación Estratégica",
        "Despliegue Rápido",
        "Construcción Completa",
        "Ingeniería Integrada",
        "Desarrollo Web",
        "Formación y CTO",
        "Otro",
      ],
      step2: {
        timeline: "¿Qué timeline funciona para ti?",
        timelineOptions: ["Inicio Rápido", "Por Proyecto", "Continuo"],
        details: "Cuéntanos más sobre tu proyecto",
        detailsPlaceholder:
          "¿Qué desafíos enfrentas? ¿Cuáles son tus objetivos?",
      },
      or: "O contáctame directamente",
      schedule: "Agendar Llamada",
    },
    footer: {
      tagline: "Creando soluciones inteligentes",
      rights: "Todos los derechos reservados",
    },
  },
};

// ===== SUBTLE BACKGROUND =====
function AnimatedBackground() {
  return (
    <div className="animated-bg">
      <div className="ambient-glow" />
      <div className="noise-layer" />
    </div>
  );
}

// ===== TYPEWRITER =====
function TypeWriter({ words }) {
  const [index, setIndex] = useState(0);
  const [text, setText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const word = words[index];
    const timeout = setTimeout(
      () => {
        if (!isDeleting) {
          setText(word.substring(0, text.length + 1));
          if (text === word) setTimeout(() => setIsDeleting(true), 2500);
        } else {
          setText(word.substring(0, text.length - 1));
          if (text === "") {
            setIsDeleting(false);
            setIndex((prev) => (prev + 1) % words.length);
          }
        }
      },
      isDeleting ? 40 : 80
    );
    return () => clearTimeout(timeout);
  }, [text, isDeleting, index, words]);

  return (
    <span className="typewriter">
      {text}
      <motion.span
        className="cursor"
        animate={{ opacity: [1, 0] }}
        transition={{ duration: 0.8, repeat: Infinity, ease: "steps(1)" }}
      />
    </span>
  );
}

// ===== SECTION REVEAL =====
function SectionReveal({
  children,
  className = "",
  direction = "up",
  delay = 0,
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  const directions = {
    up: { x: 0, y: 60 },
    left: { x: -80, y: 0 },
    right: { x: 80, y: 0 },
  };

  const offset = directions[direction] || directions.up;

  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, ...offset }}
      animate={
        isInView ? { opacity: 1, x: 0, y: 0 } : { opacity: 0, ...offset }
      }
      transition={{ duration: 0.7, delay, ease: [0.25, 0.1, 0.25, 1] }}
    >
      {children}
    </motion.div>
  );
}

// ===== STAGGER CHILDREN =====
function StaggerContainer({ children, className = "", staggerDelay = 0.1 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <motion.div
      ref={ref}
      className={className}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={{
        visible: { transition: { staggerChildren: staggerDelay } },
        hidden: {},
      }}
    >
      {children}
    </motion.div>
  );
}

function StaggerItem({ children, className = "" }) {
  return (
    <motion.div
      className={className}
      variants={{
        hidden: { opacity: 0, y: 40 },
        visible: {
          opacity: 1,
          y: 0,
          transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] },
        },
      }}
    >
      {children}
    </motion.div>
  );
}

// ===== MAGNETIC BUTTON =====
function MagneticButton({ children, className = "", onClick, href }) {
  const ref = useRef(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const handleMouse = (e) => {
    const { clientX, clientY } = e;
    const { left, top, width, height } = ref.current.getBoundingClientRect();
    const x = (clientX - left - width / 2) * 0.2;
    const y = (clientY - top - height / 2) * 0.2;
    setPosition({ x, y });
  };

  const reset = () => setPosition({ x: 0, y: 0 });

  const props = {
    ref,
    className: `magnetic-btn ${className}`,
    onMouseMove: handleMouse,
    onMouseLeave: reset,
    style: { transform: `translate(${position.x}px, ${position.y}px)` },
  };

  if (href) {
    return (
      <motion.a
        {...props}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        whileTap={{ scale: 0.98 }}
      >
        {children}
      </motion.a>
    );
  }

  return (
    <motion.button {...props} onClick={onClick} whileTap={{ scale: 0.98 }}>
      {children}
    </motion.button>
  );
}

// ===== TIMELINE WITH SCROLL DOT =====
function TimelineWithScrollDot({ items }) {
  const scrollRef = useRef(null);
  const dotRef = useRef(null);
  const lineRef = useRef(null);

  useEffect(() => {
    const container = scrollRef.current;
    const dot = dotRef.current;
    const line = lineRef.current;
    if (!container || !dot || !line) return;

    const handleScroll = () => {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      const maxScroll = scrollWidth - clientWidth;
      const progress = maxScroll > 0 ? (scrollLeft / maxScroll) * 100 : 0;

      // Direct DOM updates for smooth animation
      dot.style.left = `${progress}%`;
      line.style.setProperty("--progress", `${progress}%`);
    };

    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="timeline-wrapper">
      <div className="timeline-years">
        {["2026", "2025", "2024", "2023", "2022", "2021", "2020"].map(
          (year) => (
            <span key={year} className="timeline-year">
              {year}
            </span>
          )
        )}
      </div>
      <div className="timeline-line" ref={lineRef}>
        <div className="timeline-dot" ref={dotRef} />
      </div>
      <div className="timeline" ref={scrollRef}>
        {items.map((job, i) => (
          <motion.div
            key={i}
            className="timeline-item"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            viewport={{ once: true }}
          >
            <div className="timeline-marker" />
            <div className="timeline-content">
              <span className="timeline-period">{job.period}</span>
              <h3 className="timeline-role">{job.role}</h3>
              <span className="timeline-company">{job.company}</span>
              <span className="timeline-highlight">{job.highlight}</span>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

// ===== MAIN APP =====
export default function App() {
  const [theme, setTheme] = useState(
    () => localStorage.getItem("theme") || "light"
  );
  const [lang, setLang] = useState(() => localStorage.getItem("lang") || "en");
  const [menuOpen, setMenuOpen] = useState(false);
  const [formStep, setFormStep] = useState(1);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    service: "",
    timeline: "",
    message: "",
  });
  const { scrollY } = useScroll();
  const headerBg = useTransform(
    scrollY,
    [0, 100],
    [
      "rgba(0,0,0,0)",
      theme === "dark" ? "rgba(10,10,12,0.95)" : "rgba(255,255,255,0.95)",
    ]
  );
  const headerBlur = useTransform(
    scrollY,
    [0, 100],
    ["blur(0px)", "blur(20px)"]
  );
  const t = translations[lang];

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("theme", theme);
  }, [theme]);

  useEffect(() => {
    localStorage.setItem("lang", lang);
  }, [lang]);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleNext = () => {
    if (formData.name && formData.email && formData.service) {
      setFormStep(2);
    }
  };

  const handleBack = () => {
    setFormStep(1);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(
      `Consulting Inquiry: ${formData.service}${
        formData.timeline ? ` - ${formData.timeline}` : ""
      }`
    );
    let body = `Hello Andre,\n\n`;
    body += `I'm interested in: ${formData.service}\n`;
    if (formData.timeline) {
      body += `Timeline: ${formData.timeline}\n`;
    }
    body += `\nProject Details:\n${formData.message}\n\n`;
    body += `Best regards,\n${formData.name}\n${formData.email}`;

    const encodedBody = encodeURIComponent(body);
    window.location.href = `mailto:andrelacketech@gmail.com?subject=${subject}&body=${encodedBody}`;
  };

  return (
    <div
      className="app"
      data-theme={theme}
      itemScope
      itemType="https://schema.org/WebPage"
    >
      <AnimatedBackground />

      {/* Navigation */}
      <motion.header
        className="header"
        style={{ background: headerBg, backdropFilter: headerBlur }}
      >
        <nav className="nav">
          <motion.button
            className="nav-logo"
            onClick={() => scrollTo("home")}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            andre<span className="accent">.</span>
          </motion.button>

          <div className="nav-actions">
            <motion.button
              className="icon-btn"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              aria-label="Toggle theme"
            >
              <AnimatePresence mode="wait">
                {theme === "dark" ? (
                  <motion.div
                    key="sun"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Sun size={18} />
                  </motion.div>
                ) : (
                  <motion.div
                    key="moon"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Moon size={18} />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.button>

            <motion.button
              className="lang-btn"
              onClick={() => setLang(lang === "en" ? "es" : "en")}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {lang === "en" ? "ES" : "EN"}
            </motion.button>

            <motion.button
              className="menu-btn"
              onClick={() => setMenuOpen(!menuOpen)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              aria-label="Toggle menu"
            >
              <AnimatePresence mode="wait">
                {menuOpen ? (
                  <motion.div
                    key="x"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                  >
                    <X size={20} />
                  </motion.div>
                ) : (
                  <motion.div
                    key="menu"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                  >
                    <Menu size={20} />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.button>
          </div>

          <ul className="nav-links">
            {Object.entries(t.nav).map(([key, label]) => (
              <li key={key}>
                <motion.button
                  onClick={() => scrollTo(key)}
                  whileHover={{ color: "var(--accent)" }}
                  transition={{ duration: 0.2 }}
                >
                  {label}
                </motion.button>
              </li>
            ))}
          </ul>
        </nav>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            className="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <motion.ul
              initial="hidden"
              animate="visible"
              exit="hidden"
              variants={{
                visible: { transition: { staggerChildren: 0.1 } },
                hidden: {},
              }}
            >
              {Object.entries(t.nav).map(([key, label]) => (
                <motion.li
                  key={key}
                  variants={{
                    hidden: { opacity: 0, y: 20 },
                    visible: { opacity: 1, y: 0 },
                  }}
                >
                  <button onClick={() => scrollTo(key)}>{label}</button>
                </motion.li>
              ))}
              <motion.li
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: { opacity: 1, y: 0 },
                }}
                className="mobile-cta"
              >
                <a
                  href="https://calendly.com/andrelacke/strategy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mobile-schedule-btn"
                >
                  <Calendar size={18} />
                  <span>{t.contact.schedule}</span>
                </a>
              </motion.li>
            </motion.ul>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hero */}
      <main>
        <section
          id="home"
          className="hero"
          aria-label="Introduction"
          itemScope
          itemType="https://schema.org/Person"
        >
          <div className="hero-content">
            <motion.p
              className="hero-greeting"
              initial={{ opacity: 0, x: -60 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                duration: 0.7,
                delay: 0.2,
                ease: [0.25, 0.1, 0.25, 1],
              }}
            >
              {t.hero.greeting}
            </motion.p>
            <motion.h1
              className="hero-name"
              itemProp="name"
              initial={{ opacity: 0, x: 80 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                duration: 0.7,
                delay: 0.35,
                ease: [0.25, 0.1, 0.25, 1],
              }}
            >
              {t.hero.name}
            </motion.h1>
            <motion.div
              className="hero-role"
              initial={{ opacity: 0, x: -60 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                duration: 0.7,
                delay: 0.5,
                ease: [0.25, 0.1, 0.25, 1],
              }}
            >
              <TypeWriter words={t.hero.roles} />
            </motion.div>
            <motion.p
              className="hero-description"
              itemProp="description"
              initial={{ opacity: 0, x: 60 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                duration: 0.7,
                delay: 0.65,
                ease: [0.25, 0.1, 0.25, 1],
              }}
            >
              {t.hero.description}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, x: -60 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                duration: 0.7,
                delay: 0.8,
                ease: [0.25, 0.1, 0.25, 1],
              }}
            >
              <MagneticButton
                className="btn-primary"
                onClick={() => scrollTo("contact")}
              >
                <span>{t.hero.cta}</span>
                <ArrowRight size={18} />
              </MagneticButton>
            </motion.div>
          </div>
          <motion.button
            className="scroll-hint"
            onClick={() => scrollTo("consulting")}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2 }}
          >
            <span>{t.hero.scroll}</span>
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <ChevronDown size={20} />
            </motion.div>
          </motion.button>
        </section>

        {/* AI Consulting */}
        <section
          id="consulting"
          className="section consulting"
          aria-label="AI Consulting Services"
          itemScope
          itemType="https://schema.org/Service"
        >
          <meta itemProp="serviceType" content="AI Consulting" />
          <meta itemProp="provider" content="Andre Lacke" />
          <div className="container">
            <div className="consulting-intro">
              <SectionReveal className="consulting-header" direction="left">
                <span className="section-label">
                  {t.consulting.label || "Consulting"}
                </span>
                <h2 className="section-title">
                  {t.consulting.title}{" "}
                  <span className="accent">{t.consulting.titleAccent}</span>
                </h2>
                <p className="consulting-lead">{t.consulting.matrixIntro}</p>
              </SectionReveal>
              <SectionReveal
                className="consulting-industries"
                direction="right"
                delay={0.15}
              >
                {t.aiFor.industries.map((industry, i) => (
                  <span key={i} className="industry-tag">
                    {industry}
                  </span>
                ))}
              </SectionReveal>
            </div>

            <div className="engagement-matrix">
              {/* Timeline headers */}
              <div className="matrix-header">
                <div className="matrix-corner" />
                {t.consulting.timelines.map((timeline, i) => (
                  <div key={i} className="timeline-label">
                    <span className="timeline-dot-indicator" />
                    {timeline}
                  </div>
                ))}
              </div>

              {/* Strategy track */}
              <motion.div
                className="matrix-row"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
              >
                <div className="track-label">
                  <span className="track-name">
                    {t.consulting.tracks.strategy.label}
                  </span>
                  <span className="track-desc">
                    {t.consulting.tracks.strategy.description}
                  </span>
                </div>
                {t.consulting.tracks.strategy.options.map((opt, i) => (
                  <motion.div
                    key={i}
                    className="matrix-cell"
                    whileHover={{ scale: 1.02, y: -4 }}
                    transition={{ duration: 0.2 }}
                  >
                    <h4>{opt.title}</h4>
                    <p>{opt.desc}</p>
                  </motion.div>
                ))}
              </motion.div>

              {/* Connecting line */}
              <div className="matrix-connector">
                <svg viewBox="0 0 100 20" preserveAspectRatio="none">
                  <path
                    d="M0,10 Q25,0 50,10 T100,10"
                    stroke="currentColor"
                    fill="none"
                    strokeWidth="1"
                    strokeDasharray="4 2"
                  />
                </svg>
                <span className="connector-label">+</span>
              </div>

              {/* Implementation track */}
              <motion.div
                className="matrix-row"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
              >
                <div className="track-label">
                  <span className="track-name">
                    {t.consulting.tracks.implementation.label}
                  </span>
                  <span className="track-desc">
                    {t.consulting.tracks.implementation.description}
                  </span>
                </div>
                {t.consulting.tracks.implementation.options.map((opt, i) => (
                  <motion.div
                    key={i}
                    className="matrix-cell"
                    whileHover={{ scale: 1.02, y: -4 }}
                    transition={{ duration: 0.2 }}
                  >
                    <h4>{opt.title}</h4>
                    <p>{opt.desc}</p>
                  </motion.div>
                ))}
              </motion.div>

              <p className="path-note">{t.consulting.pathNote}</p>
            </div>

            <SectionReveal className="consulting-cta-section" direction="left">
              <MagneticButton
                href="https://calendly.com/andrelacke/strategy"
                className="btn-primary btn-large"
              >
                <Calendar size={20} />
                <span>{t.consulting.cta}</span>
              </MagneticButton>
              <p className="cta-subtext">{t.consulting.ctaSubtext}</p>
            </SectionReveal>
          </div>
        </section>

        {/* About */}
        <section
          id="about"
          className="section about"
          aria-label="About Andre Lacke"
        >
          <div className="container">
            <SectionReveal direction="right">
              <span className="section-label">{t.about.label}</span>
              <h2 className="section-title">
                {t.about.title}{" "}
                <span className="accent">{t.about.titleAccent}</span>
              </h2>
            </SectionReveal>
            <div className="about-grid">
              <SectionReveal
                className="about-content"
                direction="left"
                delay={0.1}
              >
                <p>{t.about.bio}</p>
                <p>{t.about.bio2}</p>
              </SectionReveal>
              <div className="about-stats-diagonal">
                {t.about.stats.map((stat, i) => (
                  <SectionReveal
                    key={i}
                    className="stat-diagonal-item"
                    direction="right"
                    delay={0.2 + i * 0.1}
                  >
                    <span className="stat-diagonal-value">{stat.value}</span>
                    <span className="stat-diagonal-label">{stat.label}</span>
                  </SectionReveal>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Services */}
        <section
          id="services"
          className="section services"
          aria-label="Professional Services"
          itemScope
          itemType="https://schema.org/ItemList"
        >
          <meta
            itemProp="name"
            content="AI Consulting and Development Services"
          />
          <div className="container">
            <SectionReveal direction="left">
              <span className="section-label">{t.services.label}</span>
              <h2 className="section-title">
                {t.services.title}{" "}
                <span className="accent">{t.services.titleAccent}</span>
              </h2>
            </SectionReveal>
            <div className="services-list">
              {t.services.items.map((service, i) => (
                <SectionReveal
                  key={i}
                  className="service-row"
                  direction={i % 2 === 0 ? "left" : "right"}
                  delay={i * 0.08}
                >
                  <article
                    itemScope
                    itemType="https://schema.org/Service"
                    itemProp="itemListElement"
                  >
                    <meta itemProp="position" content={String(i + 1)} />
                    <div className="service-header">
                      <span className="service-number">0{i + 1}</span>
                      <h3 className="service-title" itemProp="name">
                        {service.title}
                      </h3>
                    </div>
                    <p className="service-desc" itemProp="description">
                      {service.description}
                    </p>
                    <div className="service-tags">
                      {service.features.map((f, j) => (
                        <span key={j} className="service-tag">
                          {f}
                        </span>
                      ))}
                    </div>
                  </article>
                </SectionReveal>
              ))}
            </div>
            <SectionReveal className="services-cta" direction="right">
              <MagneticButton
                onClick={() => scrollTo("contact")}
                className="btn-outline"
              >
                <span>{t.services.ctaButton}</span>
                <ArrowRight size={18} />
              </MagneticButton>
            </SectionReveal>
          </div>
        </section>

        {/* Latest Projects */}
        <section
          id="projects"
          className="section latest-projects"
          aria-label="Portfolio Projects"
        >
          <div className="container">
            <SectionReveal direction="right">
              <span className="section-label">{t.latestProjects.label}</span>
              <h2 className="section-title">
                {t.latestProjects.title}{" "}
                <span className="accent">{t.latestProjects.titleAccent}</span>
              </h2>
            </SectionReveal>
            <StaggerContainer className="projects-showcase" staggerDelay={0.15}>
              {t.latestProjects.items.map((project, i) => (
                <StaggerItem key={i}>
                  <motion.a
                    href={project.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="project-card"
                    whileHover={{ y: -12, scale: 1.02 }}
                    transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
                  >
                    <div className="project-frame">
                      <iframe
                        src={project.url}
                        title={project.name}
                        loading="lazy"
                      />
                    </div>
                    <div className="project-label">
                      <span>{project.name}</span>
                      <ExternalLink size={12} />
                    </div>
                  </motion.a>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>

        {/* Experience Timeline */}
        <section
          id="experience"
          className="section experience"
          aria-label="Professional Experience"
        >
          <div className="container">
            <SectionReveal direction="left">
              <span className="section-label">{t.experience.label}</span>
              <h2 className="section-title">
                {t.experience.title}{" "}
                <span className="accent">{t.experience.titleAccent}</span>
              </h2>
            </SectionReveal>
            <TimelineWithScrollDot items={t.experience.items} />
          </div>
        </section>

        {/* Contact */}
        <section
          id="contact"
          className="section contact"
          aria-label="Contact Information"
          itemScope
          itemType="https://schema.org/ContactPage"
        >
          <div className="container">
            <div className="contact-grid">
              <SectionReveal className="contact-info" direction="left">
                <span className="section-label">{t.contact.label}</span>
                <h2 className="section-title">
                  {t.contact.title}{" "}
                  <span className="accent">{t.contact.titleAccent}</span>
                </h2>
                <p className="contact-subtitle">{t.contact.subtitle}</p>
                <div className="contact-links">
                  <p className="contact-or">{t.contact.or}</p>
                  <a
                    href="mailto:andrelacketech@gmail.com"
                    className="contact-email"
                  >
                    <Mail size={18} />
                    <span>andrelacketech@gmail.com</span>
                  </a>
                  <MagneticButton
                    href="https://calendly.com/andrelacke/strategy"
                    className="btn-outline schedule-btn"
                  >
                    <Calendar size={18} />
                    <span>{t.contact.schedule}</span>
                  </MagneticButton>
                  <div className="social-links">
                    <motion.a
                      href="https://linkedin.com/in/andre-l001"
                      target="_blank"
                      rel="noopener noreferrer"
                      whileHover={{ scale: 1.1, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Linkedin size={20} />
                    </motion.a>
                    <motion.a
                      href="https://github.com/AndreLCanada"
                      target="_blank"
                      rel="noopener noreferrer"
                      whileHover={{ scale: 1.1, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Github size={20} />
                    </motion.a>
                  </div>
                </div>
              </SectionReveal>
              <SectionReveal direction="right" delay={0.15}>
                <div className="contact-form-wrapper">
                  <form className="contact-form" onSubmit={handleSubmit}>
                    <div className="form-steps-container">
                      {/* Step 1 */}
                      <motion.div
                        className="form-step"
                        initial={false}
                        animate={{
                          x: formStep === 1 ? 0 : "-100%",
                          opacity: formStep === 1 ? 1 : 0,
                          pointerEvents: formStep === 1 ? "auto" : "none",
                        }}
                        transition={{
                          duration: 0.4,
                          ease: [0.25, 0.1, 0.25, 1],
                        }}
                      >
                        <div className="form-row">
                          <div className="form-group">
                            <label htmlFor="name">{t.contact.form.name}</label>
                            <input
                              type="text"
                              id="name"
                              value={formData.name}
                              onChange={(e) =>
                                handleInputChange("name", e.target.value)
                              }
                              required
                            />
                          </div>
                          <div className="form-group">
                            <label htmlFor="email">
                              {t.contact.form.email}
                            </label>
                            <input
                              type="email"
                              id="email"
                              value={formData.email}
                              onChange={(e) =>
                                handleInputChange("email", e.target.value)
                              }
                              required
                            />
                          </div>
                        </div>
                        <div className="form-group">
                          <label htmlFor="service">
                            {t.contact.form.service}
                          </label>
                          <select
                            id="service"
                            value={formData.service}
                            onChange={(e) =>
                              handleInputChange("service", e.target.value)
                            }
                            required
                          >
                            <option value="">—</option>
                            {t.contact.serviceOptions.map((opt, i) => (
                              <option key={i} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </select>
                        </div>
                        <MagneticButton
                          type="button"
                          className="btn-primary btn-full"
                          onClick={handleNext}
                          disabled={
                            !formData.name ||
                            !formData.email ||
                            !formData.service
                          }
                        >
                          <span>{t.contact.form.continue}</span>
                          <ArrowRight size={18} />
                        </MagneticButton>
                      </motion.div>

                      {/* Step 2 */}
                      <motion.div
                        className="form-step"
                        initial={false}
                        animate={{
                          x: formStep === 2 ? 0 : "100%",
                          opacity: formStep === 2 ? 1 : 0,
                          pointerEvents: formStep === 2 ? "auto" : "none",
                        }}
                        transition={{
                          duration: 0.4,
                          ease: [0.25, 0.1, 0.25, 1],
                        }}
                      >
                        <button
                          type="button"
                          className="form-back-btn"
                          onClick={handleBack}
                        >
                          <ArrowRight
                            size={16}
                            style={{ transform: "rotate(180deg)" }}
                          />
                          <span>{t.contact.form.back}</span>
                        </button>
                        {formData.service &&
                          t.contact.serviceOptions
                            .slice(0, 6)
                            .includes(formData.service) && (
                            <div className="form-group">
                              <label>{t.contact.step2.timeline}</label>
                              <div className="timeline-options">
                                {t.contact.step2.timelineOptions.map(
                                  (opt, i) => (
                                    <button
                                      key={i}
                                      type="button"
                                      className={`timeline-option ${
                                        formData.timeline === opt
                                          ? "active"
                                          : ""
                                      }`}
                                      onClick={() =>
                                        handleInputChange("timeline", opt)
                                      }
                                    >
                                      {opt}
                                    </button>
                                  )
                                )}
                              </div>
                            </div>
                          )}
                        <div className="form-group">
                          <label htmlFor="message">
                            {t.contact.step2.details}
                          </label>
                          <textarea
                            id="message"
                            value={formData.message}
                            onChange={(e) =>
                              handleInputChange("message", e.target.value)
                            }
                            rows="5"
                            placeholder={t.contact.step2.detailsPlaceholder}
                            required
                          />
                        </div>
                        <MagneticButton
                          type="submit"
                          className="btn-primary btn-full"
                        >
                          <span>{t.contact.form.send}</span>
                          <ArrowRight size={18} />
                        </MagneticButton>
                      </motion.div>
                    </div>
                  </form>
                </div>
              </SectionReveal>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer
        className="footer"
        itemScope
        itemType="https://schema.org/WPFooter"
      >
        <div className="container">
          <div className="footer-content">
            <span className="footer-logo">
              andre<span className="accent">.</span>
            </span>
            <p>{t.footer.tagline}</p>
            <p className="footer-copy">
              © {new Date().getFullYear()} Andre Lacke. {t.footer.rights}.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
